import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import DashboardShell from '../../components/layout/DashboardShell';
import { feedbackPlatformLabel, feedbackTypeLabel } from '../../lib/feedbackPosts/labels';
import { formatFeedbackTime } from '../../lib/feedbackPosts/formatTime';
import {
  subscribeFeedbackPost,
  subscribeFeedbackComments,
  addFeedbackComment,
  updateFeedbackPostStatus,
  deleteFeedbackPost,
} from '../../lib/feedbackPosts/firestore';
import { validateCommentBody } from '../../lib/feedbackPosts/validation';
import EditFeedbackPostModal from '../../components/creatorFeedback/EditFeedbackPostModal';
import ConfirmDialog from '../../components/creatorFeedback/ConfirmDialog';
import FeedbackCommentItem from '../../components/creatorFeedback/FeedbackCommentItem';

export default function FeedbackPostDetailPage({ postId, onNavigate }) {
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(() => Boolean(postId));
  const [error, setError] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [commentError, setCommentError] = useState(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (!postId) return undefined;
    setLoading(true);
    setError(null);
    const unsubPost = subscribeFeedbackPost(
      postId,
      (p) => {
        setPost(p);
        if (p) setLoading(false);
        else {
          setLoading(false);
          setError('This post was removed or does not exist.');
        }
      },
      (err) => {
        setError(err.message || 'Could not load post.');
        setLoading(false);
      }
    );
    const unsubComments = subscribeFeedbackComments(
      postId,
      setComments,
      () => {}
    );
    return () => {
      unsubPost();
      unsubComments();
    };
  }, [postId]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      setCommentError('Sign in to comment.');
      return;
    }
    if (submittingComment) return;
    const v = validateCommentBody(commentText);
    if (!v.ok) {
      setCommentError(v.message);
      return;
    }
    setCommentError(null);
    setSubmittingComment(true);
    try {
      await addFeedbackComment({
        postId,
        authorId: user.uid,
        body: v.body,
      });
      setCommentText('');
    } catch (err) {
      setCommentError(err?.message || 'Could not post comment.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const toggleClosed = async () => {
    if (!user || !post || post.authorId !== user.uid || statusBusy) return;
    setStatusBusy(true);
    try {
      await updateFeedbackPostStatus(
        postId,
        user.uid,
        post.status === 'open' ? 'closed' : 'open'
      );
    } catch (err) {
      setCommentError(err?.message || 'Could not update status.');
    } finally {
      setStatusBusy(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!user || !post || post.authorId !== user.uid || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await deleteFeedbackPost(post.id, user.uid);
      setDeleteConfirmOpen(false);
      onNavigate('feedback-feed');
    } catch (err) {
      console.error('Delete post failed:', err);
    } finally {
      setDeleteBusy(false);
    }
  };

  if (!postId) {
    return (
      <DashboardShell onNavigate={onNavigate}>
        <div className="max-w-3xl mx-auto text-center py-12">
          <p className="text-slate-600">This feedback post could not be loaded.</p>
          <button
            type="button"
            onClick={() => onNavigate('feedback-feed')}
            className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Back to feedback
          </button>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell onNavigate={onNavigate}>
      <div className="max-w-3xl mx-auto">
        <button
          type="button"
          onClick={() => onNavigate('feedback-feed')}
          className="mb-6 text-sm font-medium text-blue-600 hover:text-blue-800"
        >
          Back to feedback
        </button>

        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-slate-500">
            <div className="spinner mx-auto mb-3" />
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 text-sm">
            {error}
          </div>
        )}

        {post && !loading && (
          <>
            <article className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  {post.authorUsername ? (
                    <button
                      type="button"
                      onClick={() => onNavigate('u/' + post.authorUsername)}
                      className="flex items-center gap-3 rounded-lg -m-1 p-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-ihTeal/50 focus:ring-inset transition-colors text-left"
                      aria-label={`View ${post.authorDisplayName}'s profile`}
                    >
                      <div className="h-12 w-12 overflow-hidden rounded-full bg-slate-100 border border-slate-200">
                        {post.authorPhotoURL ? (
                          <img
                            src={post.authorPhotoURL}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-slate-500 font-medium">
                            {post.authorDisplayName?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {post.authorDisplayName}
                        </p>
                        <p className="text-sm text-slate-500">@{post.authorUsername}</p>
                      </div>
                    </button>
                  ) : (
                    <>
                      <div className="h-12 w-12 overflow-hidden rounded-full bg-slate-100 border border-slate-200">
                        {post.authorPhotoURL ? (
                          <img
                            src={post.authorPhotoURL}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-slate-500 font-medium">
                            {post.authorDisplayName?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {post.authorDisplayName}
                        </p>
                        <p className="text-sm text-slate-500">@{post.authorUsername}</p>
                      </div>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {user && post.authorId === user.uid && (
                    <div className="relative" ref={menuRef}>
                      <button
                        type="button"
                        onClick={() => setMenuOpen((prev) => !prev)}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-ihTeal/40 focus:ring-inset"
                        aria-label="Post actions"
                        aria-expanded={menuOpen}
                        aria-haspopup="true"
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>
                      {menuOpen && (
                        <div
                          className="absolute right-0 top-full z-20 mt-1 w-40 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                          role="menu"
                        >
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setMenuOpen(false);
                              setEditModalOpen(true);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setMenuOpen(false);
                              setDeleteConfirmOpen(true);
                            }}
                            className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                    {feedbackTypeLabel(post.feedbackType)}
                  </span>
                  <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800">
                    {feedbackPlatformLabel(post.platform)}
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      post.status === 'open'
                        ? 'bg-emerald-50 text-emerald-800'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {post.status === 'open' ? 'Open' : 'Closed'}
                  </span>
                </div>
              </div>

              <h1 className="mt-6 text-2xl font-bold text-slate-900">{post.title}</h1>
              <p className="mt-4 text-slate-700 whitespace-pre-wrap leading-relaxed">
                {post.description}
              </p>

              {post.contentUrl && (
                <p className="mt-4">
                  <a
                    href={post.contentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 break-all"
                  >
                    {post.contentUrl}
                  </a>
                </p>
              )}

              {post.feedbackPrompt && (
                <div className="mt-6 rounded-lg bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-950">
                  <span className="font-semibold">They asked: </span>
                  {post.feedbackPrompt}
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-500 border-t border-slate-100 pt-4">
                <span>{post.commentCount} comments</span>
                <span>{post.participantCount} people in thread</span>
                <span>Updated {formatFeedbackTime(post.lastActivityAt)}</span>
              </div>

              {user && post.authorId === user.uid && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={toggleClosed}
                    disabled={statusBusy}
                    className="text-sm font-medium text-slate-600 hover:text-slate-900 underline disabled:opacity-50"
                  >
                    {post.status === 'open'
                      ? 'Mark as closed'
                      : 'Reopen for feedback'}
                  </button>
                </div>
              )}
            </article>

            <section className="mt-10">
              <h2 className="text-lg font-semibold text-slate-900">Comments</h2>

              {post.status === 'closed' && (
                <p className="mt-2 text-sm text-slate-500">
                  This post is closed. New comments are disabled.
                </p>
              )}

              {post.status === 'open' && user && (
                <form onSubmit={handleComment} className="mt-4">
                  {commentError && (
                    <p className="mb-2 text-sm text-red-600" role="alert">
                      {commentError}
                    </p>
                  )}
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows={4}
                    placeholder="Share constructive, specific feedback."
                    disabled={submittingComment}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-slate-50"
                  />
                  <button
                    type="submit"
                    disabled={submittingComment}
                    className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {submittingComment ? 'Posting…' : 'Post comment'}
                  </button>
                </form>
              )}

              {post.status === 'open' && !user && (
                <p className="mt-4 text-sm text-slate-600">
                  Sign in to leave a comment.
                </p>
              )}

              <ul className="mt-8 space-y-4">
                {comments.length === 0 && (
                  <li className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-600">
                    No comments yet. Be the first to help out.
                  </li>
                )}
                {comments.map((c) => (
                  <FeedbackCommentItem
                    key={c.id}
                    comment={c}
                    postId={postId}
                    currentUserId={user?.uid ?? null}
                    onNavigate={onNavigate}
                    variant="comfortable"
                  />
                ))}
              </ul>
            </section>

            <EditFeedbackPostModal
              post={post}
              isOpen={editModalOpen}
              onClose={() => setEditModalOpen(false)}
              onSuccess={() => setEditModalOpen(false)}
            />
            <ConfirmDialog
              isOpen={deleteConfirmOpen}
              onClose={() => setDeleteConfirmOpen(false)}
              title="Delete this post?"
              body="This cannot be undone."
              confirmLabel="Delete"
              cancelLabel="Cancel"
              danger
              busy={deleteBusy}
              onConfirm={handleDeleteConfirm}
            />
          </>
        )}
      </div>
    </DashboardShell>
  );
}
